import { useMemo } from 'react';
import { ZodError } from 'zod';

/**
 * This hook allows components to assert against invalid data, throwing on the client, but not the server
 *
 * Validating in this way still allows error boundaries to work on the client to
 * make it easier to debug invalid data, while allowing the server to recover by
 * not rendering the component.
 *
 * @template T
 * @param {import('zod').ZodSchema<T>} schema - The Zod schema to validate against.
 * @param {unknown} data - The data to validate.
 * @returns {[true, T] | [false, undefined]} - Validated props if valid, null if invalid on the server.
 * @example
 * function MyComponent(props) {
 *   const [isPayloadValid, payload] = useAssertValid(mySchema, props.payload);
 *   if (!isValid) {
 *     return null;
 *   }
 * }
 */
export function useAssertValid(schema, data) {
  return useMemo(() => {
    const result = schema.safeParse(data);

    if (!result.success) {
      if (typeof window !== 'undefined') {
        throw new ZodError(result.error.errors);
      } else {
        return [false, undefined];
      }
    }

    return [true, result.data];
  }, [schema, data]);
}
